// src/stores/sermon.ts
import {
  addDoc,
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  serverTimestamp,
  writeBatch,
  runTransaction,
  query,
  where,
  limit,
  documentId,
  type FirestoreDataConverter,
} from "firebase/firestore";
import { db } from "@/firebaseConfig";
import { defaultScheduleItemDuration } from "@/utils/scheduleItemTypes";
import type {
  ActionTree,
  GetterTree,
  Module,
  MutationTree,
  ActionContext,
} from "vuex";

// ----- Types -----

// A slide is a scripture passage (its text split over `content`) or one of
// the organisation's library images (`slideImages`). Slides from before
// image slides have no `type` and are scripture.
export type SermonSlideType = "scripture" | "image";

export interface SermonSlide {
  id: string;
  type?: SermonSlideType;
  title: string;
  background: string;
  content: string[];
  imageId?: string;
  url?: string;
  storagePath?: string;
}

export interface Sermon {
  id?: string;
  title: string;
  preacher: string;
  description: string;
  slides: SermonSlide[];
  /** Kept with `slides`: the library images they show, for an array-contains query. */
  imageIds?: string[];
  account: number;
  createdBy: string | null;
  notes?: string;
  // allow extra fields from Firestore safely
  [key: string]: unknown;
}

export interface SermonState {
  fetching: { value: boolean };
  sermon: { data: Sermon | null };
}

// If you have a real RootState somewhere, import and use that instead of `any`
export type RootState = any;

// Helper type for slide-like inputs coming from the UI
export type SermonSlideInput = Partial<SermonSlide> & {
  content?: unknown;
};

// ----- helpers -----
const newId = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return doc(collection(db, "_ids")).id; // Firestore-style id without writing
};

const normalizeSlide = (s?: SermonSlideInput | null): SermonSlide => {
  const base = {
    id: String(s?.id ?? newId()),
    title: String(s?.title ?? "").trim(),
    background: String(s?.background ?? "").trim(),
  };
  if (s?.type === "image") {
    return {
      ...base,
      type: "image",
      content: [],
      imageId: String(s.imageId ?? ""),
      url: String(s.url ?? ""),
      storagePath: String(s.storagePath ?? ""),
    };
  }
  return {
    ...base,
    content: Array.isArray(s?.content) ? s.content.map(String) : [],
  };
};

// Deleting a library image looks for sermons showing it by this list, as
// Firestore can not query a field inside an array of slides.
const imageIdsOf = (slides: SermonSlide[]): string[] => [
  ...new Set(
    slides.flatMap((s) => (s.type === "image" && s.imageId ? [s.imageId] : [])),
  ),
];

// ----- state -----
const state = (): SermonState => ({
  fetching: { value: false },
  sermon: { data: null },
});

// ----- getters -----
const getters: GetterTree<SermonState, RootState> = {
  active(state): Sermon | null {
    return state.sermon.data;
  },
  isFetching(state): boolean {
    return state.fetching.value;
  },
};

// ----- mutations -----
const mutations: MutationTree<SermonState> = {
  SET_FETCHING(state, v: boolean) {
    state.fetching.value = !!v;
  },
  SET_SERMON(state, payload: Sermon | null) {
    state.sermon.data = payload;
  },
  CLEAR_SERMON(state) {
    state.sermon.data = null;
  },
  UPDATE_ACTIVE_SERMON(state, patch: Partial<Sermon>) {
    if (!state.sermon?.data) return;
    state.sermon.data = { ...state.sermon.data, ...patch };
  },
  APPEND_SLIDE_TO_ACTIVE_SERMON(state, slide: SermonSlide) {
    if (!state.sermon?.data) return;
    const cur = state.sermon.data;
    const slides = Array.isArray(cur.slides) ? [...cur.slides, slide] : [slide];
    state.sermon.data = { ...cur, slides };
  },
  UPDATE_ACTIVE_SERMON_SLIDE_BY_ID(
    state,
    { slideId, patch }: { slideId: string; patch: Partial<SermonSlide> },
  ) {
    const cur = state.sermon?.data;
    if (!cur || !Array.isArray(cur.slides)) return;
    const i = cur.slides.findIndex((s) => s?.id === slideId);
    if (i === -1) return;

    const merged: SermonSlide = {
      ...(cur.slides[i] as SermonSlide),
      ...(patch as Partial<SermonSlide>),
    };

    if (patch?.content !== undefined) {
      merged.content = Array.isArray(patch.content)
        ? patch.content.map(String)
        : [];
    }

    const slides = cur.slides.slice();
    slides[i] = merged;
    state.sermon.data = { ...cur, slides };
  },
  REPLACE_ACTIVE_SERMON_SLIDES(state, slides: SermonSlide[]) {
    if (!state.sermon?.data) return;
    state.sermon.data = { ...state.sermon.data, slides };
  },
};

// ----- actions -----
const API_BASE_URL = "https://worshipr-api.vercel.app";

interface FetchByIdPayload {
  id: string;
  callback?: (sermon: Sermon) => void;
}

interface CreatePayload {
  title: string;
  preacher: string;
  description: string;
  slides?: SermonSlideInput[];
}

interface CreateAndAddToSchedulePayload {
  eventId: string;
  data: {
    title?: string;
    preacher?: string;
    description?: string;
    slides?: SermonSlideInput[];
  };
  type: string;
  start: unknown; // Timestamp | Date | string etc.
}

interface CreateForScheduleItemPayload {
  eventId: string;
  scheduleId: string;
  title?: string;
}

interface SavePayload {
  sermonId: string;
  title?: string;
  preacher?: string;
  description?: string;
  slides?: SermonSlideInput[];
  notes?: string;
  callback?: (updated: Partial<Sermon>) => void;
}

interface AppendSlidePayload {
  sermonId: string;
  slide: SermonSlideInput;
  callback?: (inserted: SermonSlide) => void;
}

interface UpdateSlideByIdPayload {
  sermonId: string;
  slideId: string;
  patch: SermonSlideInput;
  callback?: (updated: SermonSlide) => void;
}

interface ReorderSlidesPayload {
  sermonId: string;
  orderIds: string[];
  callback?: (slides: SermonSlide[]) => void;
}

interface ReplaceSlidesPayload {
  sermonId: string;
  slides: SermonSlideInput[];
  callback?: (slides: SermonSlide[]) => void;
}

const actions: ActionTree<SermonState, RootState> = {
  // --- READ (with account guard) ---
  async fetchById(
    { commit, rootGetters }: ActionContext<SermonState, RootState>,
    { id, callback }: FetchByIdPayload,
  ) {
    if (!id) throw new Error("id is required");
    commit("SET_FETCHING", true);

    try {
      const account = Number(rootGetters?.["users/user"]?.account ?? 0);
      // eslint-disable-next-line no-console

      const url = `${API_BASE_URL}/api/sermon/${encodeURIComponent(
        id,
      )}?account=${account}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        if (res.status === 404 || res.status === 403) {
          throw new Error("Not found or access denied");
        }
        throw new Error(`Failed to fetch sermon (${res.status})`);
      }

      const data: any = await res.json();

      const sermon: Sermon =
        data && typeof data === "object" && "id" in data
          ? (data as Sermon)
          : ({ id, ...(data?.content ?? data ?? {}) } as Sermon);

      commit("SET_SERMON", sermon);
      callback?.(sermon);
    } finally {
      commit("SET_FETCHING", false);
    }
  },

  // --- CREATE ---
  async create(
    { commit, rootGetters }: ActionContext<SermonState, RootState>,
    { title, preacher, description, slides = [] }: CreatePayload,
  ): Promise<string> {
    const account = Number(rootGetters?.["users/user"]?.account ?? 0);
    const uid = (rootGetters?.["users/user"]?.uid as string | null) ?? null;

    const t = String(title ?? "").trim();
    const p = String(preacher ?? "").trim();
    const d = String(description ?? "").trim();
    if (!t) throw new Error("Title is required");
    if (!p) throw new Error("Preacher is required");
    if (!d) throw new Error("Description is required");

    const ref = doc(collection(db, "sermons"));
    const normalizedSlides = Array.isArray(slides)
      ? slides.map(normalizeSlide)
      : [];

    await setDoc(ref, {
      title: t,
      preacher: p,
      description: d,
      slides: normalizedSlides,
      imageIds: imageIdsOf(normalizedSlides),
      account,
      createdBy: uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    commit("SET_SERMON", {
      id: ref.id,
      title: t,
      preacher: p,
      description: d,
      slides: normalizedSlides,
      account,
      createdBy: uid,
    } as Sermon);

    return ref.id;
  },

  // --- CREATE + ADD TO SCHEDULE (atomic) ---
  async createAndAddToSchedule(
    { rootGetters }: ActionContext<SermonState, RootState>,
    { eventId, data, type, start }: CreateAndAddToSchedulePayload,
  ): Promise<{ sermonId: string; scheduleId: string }> {
    if (!eventId) throw new Error("eventId is required");

    const account = Number(rootGetters?.["users/user"]?.account ?? 0);
    const uid = (rootGetters?.["users/user"]?.uid as string | null) ?? null;

    const title = String(data?.title ?? "").trim();
    const preacher = String(data?.preacher ?? "").trim();
    const description = String(data?.description ?? "").trim();
    const slides: SermonSlide[] = [];

    // if (!title || !preacher || !description) {
    //   throw new Error("Missing fields");
    // }

    const batch = writeBatch(db);

    const payload = {
      title,
      preacher,
      description,
      slides,
      account,
      createdBy: uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // sermon
    const sermonRef = doc(collection(db, "sermons"));
    batch.set(sermonRef, payload);

    // schedule item
    const scheduleRef = doc(collection(db, "events", eventId, "schedule"));
    batch.set(scheduleRef, {
      title,
      type,
      start,
      duration: defaultScheduleItemDuration(type),
      timestamp: serverTimestamp(),
      reference: sermonRef,
      sermonId: sermonRef.id,
    });

    await batch.commit();

    return { sermonId: sermonRef.id, scheduleId: scheduleRef.id };
  },

  // Schedule templates add sermon rows without a sermon document; create one
  // and link it to the existing row.
  async createForScheduleItem(
    { rootGetters }: ActionContext<SermonState, RootState>,
    { eventId, scheduleId, title }: CreateForScheduleItemPayload,
  ): Promise<string> {
    if (!eventId || !scheduleId) {
      throw new Error("eventId and scheduleId are required");
    }

    const account = Number(rootGetters?.["users/user"]?.account ?? 0);
    const uid = (rootGetters?.["users/user"]?.uid as string | null) ?? null;

    const batch = writeBatch(db);

    const sermonRef = doc(collection(db, "sermons"));
    batch.set(sermonRef, {
      title: String(title ?? "").trim(),
      preacher: "",
      description: "",
      slides: [],
      account,
      createdBy: uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    batch.update(doc(db, "events", eventId, "schedule", scheduleId), {
      reference: sermonRef,
      sermonId: sermonRef.id,
    });

    await batch.commit();

    return sermonRef.id;
  },

  // --- UPDATE META and/or ALL SLIDES ---
  async save(
    { commit, getters }: ActionContext<SermonState, RootState>,
    {
      sermonId,
      title,
      preacher,
      description,
      slides,
      notes,
      callback,
    }: SavePayload,
  ) {
    if (!sermonId) throw new Error("sermonId is required");
    const ref = doc(db, "sermons", sermonId);

    const trimmed: Partial<Sermon> = {
      ...(title !== undefined ? { title: String(title).trim() } : {}),
      ...(preacher !== undefined ? { preacher: String(preacher).trim() } : {}),
      ...(description !== undefined
        ? { description: String(description).trim() }
        : {}),
      ...(notes !== undefined ? { notes: String(notes).trim() } : {}),
    };

    const updated = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error("Sermon not found");

      const toUpdate: any = { ...trimmed, updatedAt: serverTimestamp() };
      if (slides !== undefined) {
        toUpdate.slides = Array.isArray(slides)
          ? slides.map(normalizeSlide)
          : [];
        toUpdate.imageIds = imageIdsOf(toUpdate.slides);
      }

      tx.update(ref, toUpdate);
      return toUpdate as Partial<Sermon>;
    });

    const active = getters.active as Sermon | null;
    if (active?.id === sermonId) {
      commit("UPDATE_ACTIVE_SERMON", updated);
    }

    callback?.(updated);
    return updated;
  },

  // --- SLIDES: APPEND ---
  async appendSlide(
    { commit, getters }: ActionContext<SermonState, RootState>,
    { sermonId, slide, callback }: AppendSlidePayload,
  ) {
    if (!sermonId) throw new Error("sermonId is required");
    const ref = doc(db, "sermons", sermonId);
    const base = normalizeSlide(slide);

    const inserted = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error("Sermon not found");

      const data = snap.data() as { slides?: SermonSlide[] };
      const slidesArr = Array.isArray(data.slides) ? data.slides.slice() : [];
      slidesArr.push(base);
      tx.update(ref, {
        slides: slidesArr,
        imageIds: imageIdsOf(slidesArr),
        updatedAt: serverTimestamp(),
      });
      return base;
    });

    const active = getters.active as Sermon | null;
    if (active?.id === sermonId) {
      commit("APPEND_SLIDE_TO_ACTIVE_SERMON", inserted);
    }

    callback?.(inserted);
    return inserted;
  },

  // --- SLIDES: UPDATE BY ID ---
  async updateSlideById(
    { commit, getters }: ActionContext<SermonState, RootState>,
    { sermonId, slideId, patch, callback }: UpdateSlideByIdPayload,
  ) {
    if (!sermonId) throw new Error("sermonId is required");
    if (!slideId) throw new Error("slideId is required");
    const ref = doc(db, "sermons", sermonId);

    const norm: Partial<SermonSlide> = {};
    if (patch?.title !== undefined) norm.title = String(patch.title).trim();
    if (patch?.background !== undefined)
      norm.background = String(patch.background).trim();
    if (patch?.content !== undefined) {
      norm.content = Array.isArray(patch.content)
        ? patch.content.map(String)
        : [];
    }
    if (patch?.imageId !== undefined) norm.imageId = String(patch.imageId);
    if (patch?.url !== undefined) norm.url = String(patch.url);
    if (patch?.storagePath !== undefined)
      norm.storagePath = String(patch.storagePath);

    const updated = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error("Sermon not found");

      const data = snap.data() as { slides?: SermonSlide[] };
      const slidesArr = Array.isArray(data.slides) ? data.slides.slice() : [];
      const i = slidesArr.findIndex((s) => s?.id === slideId);
      if (i === -1) throw new Error("Slide not found");

      slidesArr[i] = {
        ...slidesArr[i],
        ...norm,
        id: slidesArr[i].id,
      };

      tx.update(ref, {
        slides: slidesArr,
        imageIds: imageIdsOf(slidesArr),
        updatedAt: serverTimestamp(),
      });
      return slidesArr[i];
    });

    const active = getters.active as Sermon | null;
    if (active?.id === sermonId) {
      commit("UPDATE_ACTIVE_SERMON_SLIDE_BY_ID", {
        slideId,
        patch: updated,
      });
    }

    callback?.(updated);
    return updated;
  },

  // --- SLIDES: REORDER BY ARRAY OF IDS ---
  async reorderSlides(
    { commit, getters }: ActionContext<SermonState, RootState>,
    { sermonId, orderIds, callback }: ReorderSlidesPayload,
  ) {
    if (!sermonId) throw new Error("sermonId is required");
    if (!Array.isArray(orderIds))
      throw new Error("orderIds must be an array of slide ids");
    const ref = doc(db, "sermons", sermonId);

    const newSlides = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error("Sermon not found");

      const current = Array.isArray((snap.data() as any).slides)
        ? (snap.data() as any).slides.slice()
        : [];
      const map = new Map<string, SermonSlide>(
        current.map((s: SermonSlide) => [s.id, s]),
      );
      const reordered = orderIds
        .map((id) => map.get(id))
        .filter((s): s is SermonSlide => Boolean(s));

      const leftovers = current.filter(
        (s: SermonSlide) => !orderIds.includes(s.id),
      );
      const finalSlides: SermonSlide[] = [...reordered, ...leftovers];

      tx.update(ref, { slides: finalSlides, updatedAt: serverTimestamp() });
      return finalSlides;
    });

    const active = getters.active as Sermon | null;
    if (active?.id === sermonId) {
      commit("REPLACE_ACTIVE_SERMON_SLIDES", newSlides);
    }

    callback?.(newSlides);
    return newSlides;
  },

  // --- SLIDES: REPLACE ALL (normalized, preserves ids) ---
  async replaceSlides(
    { commit, getters }: ActionContext<SermonState, RootState>,
    { sermonId, slides, callback }: ReplaceSlidesPayload,
  ) {
    if (!sermonId) throw new Error("sermonId is required");
    const ref = doc(db, "sermons", sermonId);
    const normalized = Array.isArray(slides) ? slides.map(normalizeSlide) : [];

    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error("Sermon not found");
      tx.update(ref, {
        slides: normalized,
        imageIds: imageIdsOf(normalized),
        updatedAt: serverTimestamp(),
      });
    });

    const active = getters.active as Sermon | null;
    if (active?.id === sermonId) {
      commit("REPLACE_ACTIVE_SERMON_SLIDES", normalized);
    }

    callback?.(normalized);
    return normalized;
  },

  // The titles of the organisation's sermons with a slide showing this
  // library image, which may not be deleted while they do.
  async titlesShowingImage(
    { rootGetters }: ActionContext<SermonState, RootState>,
    imageId: string,
  ): Promise<string[]> {
    const account = Number(rootGetters?.["users/user"]?.account ?? 0);
    const snap = await getDocs(
      query(
        collection(db, "sermons"),
        where("account", "==", account),
        where("imageIds", "array-contains", imageId),
      ),
    );
    return snap.docs.map((d) => String(d.data().title ?? "").trim());
  },

  clearActive({ commit }) {
    commit("CLEAR_SERMON");
  },
};

// ----- module -----
const sermon: Module<SermonState, RootState> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default sermon;
